import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button
} from '@mui/material';
import { Home } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguageContext } from '../contexts/LanguageContext';

const TermsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguageContext();

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 2,
          backgroundColor: 'transparent',
          color: 'white',
          '& .MuiTypography-root': { color: 'white' },
          '& .MuiAlert-root .MuiTypography-root': { color: 'inherit' },
          '& .MuiDivider-root': { borderColor: 'rgba(255, 255, 255, 0.3)' }
        }}
      >
        <Typography variant="h3" component="h1" gutterBottom align="center" color="primary">
          {t('terms.title')}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
          {t('terms.effectiveDate')}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<Home />}
            onClick={handleGoHome}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              borderColor: 'white',
              color: 'white',
              '&:hover': {
                backgroundColor: 'white',
                color: '#6F4E37',
                borderColor: 'white'
              }
            }}
          >
            {t('terms.goHome')}
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            제1조 (목적)
          </Typography>
          <Typography variant="body1" paragraph>
            본 약관은 고도화 사회 이니셔티브(이하 "회사")가 제공하는 "He Listens to Me" 서비스(이하 "서비스")의 이용과 관련하여 회사와 이용자 간의 권리, 의무 및 책임사항을 규정함을 목적으로 합니다.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>YouTube 서비스 이용 동의:</strong> 본 서비스를 이용함으로써 이용자는 YouTube 서비스 약관(https://www.youtube.com/t/terms)에 동의하는 것으로 간주됩니다.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            제2조 (정의)
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="1. '서비스'"
                secondary="회사가 제공하는 기도·예배 관련 온라인 서비스 일체를 의미합니다."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="2. '이용자'"
                secondary="본 약관에 따라 회사가 제공하는 서비스를 받는 자를 의미합니다."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="3. '콘텐츠'"
                secondary="서비스 내에서 제공되는 모든 정보, 텍스트, 이미지, 음성 등을 의미합니다."
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            제3조 (서비스 개요 및 특성)
          </Typography>
          <Typography variant="body1" paragraph>
            본 서비스는 다음과 같은 특성을 가집니다:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="1. 회원가입 불필요"
                secondary="별도의 회원가입 절차 없이 누구나 자유롭게 이용할 수 있습니다."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="2. 개인정보 최소화"
                secondary="기도 내용의 사생활 보호를 위해 개인정보를 최소한으로 수집합니다."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="3. 임시 데이터 처리"
                secondary="모든 정보는 예배 종료 후 자동으로 삭제됩니다."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="4. AI 기반 맞춤형 예배"
                secondary="기도 분석, 성경구절 추천, 찬송/설교 구성 등은 AI를 통해 개인화되어 제공됩니다. 본 서비스가 제공하는 결과는 신앙적·영성적 안내이며 법률·의학적 자문이 아닙니다."
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            제4조 (이용 조건)
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="1. 미성년자 이용"
                secondary="미성년자는 법정대리인의 동의 하에 서비스를 이용해야 합니다."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="2. 신중한 입력"
                secondary="기도 내용 등 민감 정보 입력 시 이용자가 스스로 주의를 기울여야 합니다. 회사는 서비스 제공에 필요한 최소 범위 내에서만 처리를 지향합니다."
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            제5조 (서비스의 제공)
          </Typography>
          <Typography variant="body1" paragraph>
            1. 회사는 연중무휴, 1일 24시간 서비스를 제공함을 원칙으로 합니다.
          </Typography>
          <Typography variant="body1" paragraph>
            2. 다만, 시스템 정기점검, 증설 및 교체, 설비의 장애 또는 운영상 상당한 이유가 있는 경우 서비스의 전부 또는 일부를 제한하거나 중단할 수 있습니다.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            제6조 (결제 및 헌금)
          </Typography>
          <Typography variant="body1" paragraph>
            본 서비스에서 발생하는 결제는 이용자의 자발적 헌금(도네이션)에 기반합니다. 결제는 결제대행사 정책(예: 토스페이먼츠 등)에 따르며, 결제 내역은 결제사 시스템 기준으로 관리됩니다.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            제7조 (이용자의 의무)
          </Typography>
          <Typography variant="body1" paragraph>
            이용자는 다음 각 호의 행위를 하여서는 안 됩니다:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="1. 타인의 정보 도용"
                secondary="타인의 개인정보를 도용하거나 허위정보를 입력하는 행위"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="2. 서비스 방해"
                secondary="서비스의 안정적인 운영을 방해할 수 있는 행위"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="3. 불법 행위"
                secondary="법령 또는 공서양속에 위반되는 행위"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="4. 종교적 존중"
                secondary="기도 및 예배의 신성함을 훼손하는 부적절한 내용 입력"
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            제8조 (개인정보 보호)
          </Typography>
          <Typography variant="body1" paragraph>
            1. 회사는 관련 법령이 정하는 바에 따라 이용자의 개인정보를 보호하기 위해 노력합니다.
          </Typography>
          <Typography variant="body1" paragraph>
            2. 성별 구분은 오직 예배 준비(성경말씀, 축도 등)를 위해서만 사용되며, 다른 목적으로 사용되지 않습니다.
          </Typography>
          <Typography variant="body1" paragraph>
            3. 모든 정보는 예배 종료 후 자동으로 삭제되어 별도 저장되지 않습니다.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            제9조 (서비스 이용의 제한)
          </Typography>
          <Typography variant="body1" paragraph>
            회사는 이용자가 본 약관의 의무를 위반하거나 서비스의 정상적인 운영을 방해한 경우, 경고, 일시정지, 영구이용정지 등으로 서비스 이용을 단계적으로 제한할 수 있습니다.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            제10조 (지적재산권)
          </Typography>
          <Typography variant="body1" paragraph>
            서비스 및 이에 포함된 콘텐츠에 대한 지적재산권은 회사 또는 해당 권리자에게 귀속됩니다. 이용자는 서비스를 통해 제공되는 콘텐츠를 개인적·비상업적 용도로만 이용할 수 있으며, 무단 복제·배포·2차적 저작물 작성 등을 해서는 안 됩니다.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            제11조 (면책 및 책임 제한)
          </Typography>
          <Typography variant="body1" paragraph>
            1. 회사는 천재지변 또는 이에 준하는 불가항력으로 인하여 서비스를 제공할 수 없는 경우에는 서비스 제공에 관한 책임이 면제됩니다.
          </Typography>
          <Typography variant="body1" paragraph>
            2. 회사는 이용자의 귀책사유로 인한 서비스 이용의 장애에 대하여는 책임을 지지 않습니다.
          </Typography>
          <Typography variant="body1" paragraph>
            3. 회사는 이용자가 서비스를 이용하여 기대하는 수익을 얻지 못하거나 손실을 입은 것에 대해서는 책임을 지지 않습니다.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            제12조 (분쟁 해결)
          </Typography>
          <Typography variant="body1" paragraph>
            1. 회사는 이용자가 제기하는 정당한 의견이나 불만을 반영하고 그 피해를 보상처리하기 위하여 피해보상처리기구를 설치·운영합니다.
          </Typography>
          <Typography variant="body1" paragraph>
            2. 본 약관과 관련하여 발생한 분쟁은 대한민국 법령에 의하여 규정되고, 서울중앙지방법원을 관할법원으로 합니다.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            제13조 (약관의 효력 및 변경)
          </Typography>
          <Typography variant="body1" paragraph>
            1. 본 약관은 서비스를 이용하고자 하는 모든 이용자에 대하여 그 효력을 발생합니다.
          </Typography>
          <Typography variant="body1" paragraph>
            2. 회사는 약관의 규제에 관한 법률, 정보통신망 이용촉진 및 정보보호 등에 관한 법률 등 관련법을 위배하지 않는 범위에서 본 약관을 개정할 수 있습니다.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            제14조 (환불 정책)
          </Typography>
          <Typography variant="body1" paragraph>
            본 서비스의 환불 정책은 다음과 같습니다:
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary="1. 디지털 서비스 제공 후 환불 불가"
                secondary="예배가 시작되었거나 콘텐츠(찬송/성경/설교/축도 등)가 생성·제공된 이후에는 서비스 특성상 환불이 불가합니다. '제공'에는 예배 순서 생성, 유튜브/말씀/설교 링크 제공, 화면 상 표시 등 실질적 이용이 가능한 상태가 포함됩니다."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="2. 중복/오결제"
                secondary="중복결제 또는 오결제의 경우 결제일로부터 7일 이내 회사에 문의하시면 확인 후 전액 또는 일부 환불을 처리합니다. 증빙(거래내역, 결제ID 등) 제출이 필요할 수 있습니다."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="3. 처리 기한"
                secondary="환불 완료까지는 결제 수단 및 결제대행사(예: 토스페이먼츠) 정책에 따라 영업일 기준 3~7일 소요될 수 있습니다. 카드사·은행 처리 지연 시 추가 시간이 소요될 수 있습니다."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="4. 환불 예외"
                secondary="이용자의 약관 위반, 부정 이용 등으로 인한 결제는 환불 대상에서 제외됩니다. 무단/사기 결제의 경우 결제사 조사 결과에 따릅니다."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="5. 관련 법령 및 정책"
                secondary="환불과 관련한 기타 사항은 전자상거래 등에서의 소비자보호에 관한 법률 등 관련 법령 및 결제대행사 정책을 따릅니다."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="6. 헌금 환불"
                secondary="이용자가 결제한 헌금은 결제일로부터 7일 이내 정당한 사유와 함께 환불을 요청할 수 있습니다. 다만, 외부 전달·집행이 이미 완료된 헌금의 경우 환불이 제한될 수 있습니다."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="7. 시스템 장애로 인한 환불"
                secondary="시스템 장애로 인해 서비스 이용에 중대한 지장이 발생한 경우, 회사 확인 및 결제사 정책에 따른 절차를 거쳐 전액 또는 일부 환불합니다."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="8. 환불 요청 방법"
                secondary="문의 이메일(huney.kong@gmail.com)로 다음 정보를 보내주십시오: 성함, 결제일시, 결제수단, 결제 승인번호/거래ID, 환불 사유. 접수 후 영업일 기준 3일 내 접수 안내를 드립니다."
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="9. 부분 환불 및 수수료"
                secondary="서비스가 일부만 제공된 경우 합리적 범위에서 부분 환불이 이루어질 수 있습니다. 이용자 귀책 사유인 경우 결제대행사 수수료 등은 공제될 수 있습니다."
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button
            variant="contained"
            startIcon={<Home />}
            onClick={handleGoHome}
            sx={{
              mb: 3,
              borderRadius: 2,
              px: 4,
              py: 1.5,
              background: '#6F4E37',
              '&:hover': {
                background: '#5A3E2B',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            홈으로 가기
          </Button>
          <Typography variant="body2" color="text.secondary">
            문의사항이 있으시면 고도화 사회 이니셔티브 (huney.kong@gmail.com)로 연락주시기 바랍니다.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default TermsPage;